/**
 * ComplexTextCalc — multi-step dosing calculator, similar to simple_volume
 * but with multi-step instructions for different phases (e.g. cycling).
 */
import { useState } from 'react';
import type { Calculator } from '@reefapp/shared';
import type { ComplexTextOption } from '@reefapp/shared';

type ComplexTextCalcShape = Calculator & { kind: 'complex_text' };

export interface ComplexTextCalcProps {
  calculator: ComplexTextCalcShape;
  tankVolumeGal: number;
}

function fmt(n: number): string {
  return Number.isFinite(n) ? n.toFixed(2).replace(/\.?0+$/, '') : '—';
}

export function ComplexTextCalc({
  calculator,
  tankVolumeGal,
}: ComplexTextCalcProps) {
  const [selectedOptionIdx, setSelectedOptionIdx] = useState(0);

  const option = calculator.options[selectedOptionIdx];
  if (!option) return null;

  const dose =
    tankVolumeGal > 0
      ? (option.dose / option.per_volume) * tankVolumeGal
      : 0;
  const hasVolume = tankVolumeGal > 0;

  return (
    <div>
      <h3 className="text-lg font-semibold text-cyan-300 mb-4">
        Multi-Step Dosing Guide
      </h3>

      {/* Step selector */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-slate-400 mb-2">
          Dosing Phase:
        </label>
        <select
          value={selectedOptionIdx}
          onChange={(e) => setSelectedOptionIdx(Number(e.target.value))}
          className="block w-full rounded-md border-slate-700 bg-slate-900 py-2 px-3 text-sm text-slate-200 shadow-sm focus:border-cyan-500 focus:ring-cyan-500"
        >
          {calculator.options.map((o: ComplexTextOption, i: number) => (
            <option key={i} value={i}>
              {o.name}
            </option>
          ))}
        </select>
      </div>

      {/* Individual step result */}
      <div className="bg-slate-900 rounded-lg p-4 border border-slate-700 mb-3">
        <p className="text-sm text-slate-400">
          {option.name}:{' '}
          <span className="text-slate-300">
            {option.dose} {option.dose_unit} / {option.per_volume} gal
          </span>
        </p>

        {hasVolume ? (
          <div className="mt-3">
            <p className="text-2xl font-bold text-cyan-300">
              {fmt(dose)} {option.dose_unit}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              For {fmt(tankVolumeGal)} gallons
            </p>
          </div>
        ) : (
          <p className="mt-3 text-sm text-amber-400">
            Enter your tank volume to calculate each phase.
          </p>
        )}
      </div>

      {/* Show all phases summary */}
      <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
        <h4 className="text-sm font-semibold text-slate-300 mb-2">
          All Dosing Phases:
        </h4>
        <ul className="space-y-1">
          {calculator.options.map((o: ComplexTextOption, i: number) => {
            const phaseCalc =
              tankVolumeGal > 0
                ? (o.dose / o.per_volume) * tankVolumeGal
                : 0;
            return (
              <li key={i} className="text-xs text-slate-400 flex justify-between">
                <span>{o.name}</span>
                <span className="text-slate-300 font-mono">
                  {tankVolumeGal > 0 ? `${fmt(phaseCalc)} ${o.dose_unit}` : '—'}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}